import React from "react";
import { FaRedo, FaTrash } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "src/common/components/Button";
import { Card, CardContent } from "src/common/components/Card";
import {
	DataTable,
	DataTableSearch,
	DataTableTitle,
	DataTableToolbar,
	DataTableWrapper,
	EmptyDataTableMessage,
	RowData
} from "src/common/components/DataTable";
import { ListPage } from "src/common/components/Page";
import { FullPagePreloader } from "src/common/components/Preloader";
import { genericMessages } from "src/messages/generic";
import { messages } from "src/views/invitation-list/messages";
import { useInvitationList } from "src/views/invitation-list/useInvitationList";
import { useInvitationListColumns } from "src/views/invitation-list/useInvitationListColumns";

export const InvitationList: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	const [query, setQuery] = React.useState("");
	const columns = useInvitationListColumns();
	const { isLoading, invitations, resendInvitation, deleteInvitation } =
		useInvitationList();
	if (isLoading) {
		return <FullPagePreloader />;
	}
	return (
		<ListPage>
			<Card>
				<CardContent>
					<DataTableToolbar>
						<DataTableTitle>
							<FormattedMessage {...messages.invitationList} />
						</DataTableTitle>
						<DataTableSearch
							placeholder={formatMessage(
								genericMessages.searchOnPage
							)}
							query={query}
							setQuery={setQuery}
						/>
					</DataTableToolbar>
					<DataTableWrapper>
						<DataTable
							columns={columns}
							data={invitations as unknown as RowData[]}
							query={query}
							initialSortKey="name"
							noDataMessage={
								<EmptyDataTableMessage>
									<FormattedMessage
										{...(query === ""
											? messages.noContent
											: genericMessages.noMatches)}
									/>
								</EmptyDataTableMessage>
							}
							renderActions={({
								isLoading,
								token
							}: {
								isLoading: boolean;
								token: string;
							}) => (
								<Actions
									isLoading={isLoading}
									token={token}
									resendInvitation={resendInvitation}
									deleteInvitation={deleteInvitation}
								/>
							)}
						/>
					</DataTableWrapper>
				</CardContent>
			</Card>
		</ListPage>
	);
};

interface ActionsProps {
	isLoading: boolean;
	token: string;
	resendInvitation(token: string): unknown;
	deleteInvitation(token: string): unknown;
}

const Actions: React.FunctionComponent<ActionsProps> = ({
	isLoading,
	token,
	resendInvitation,
	deleteInvitation
}) => {
	const { formatMessage } = useIntl();
	const onResend = React.useCallback(() => {
		resendInvitation(token);
	}, [token, resendInvitation]);
	const onDelete = React.useCallback(() => {
		deleteInvitation(token);
	}, [token, deleteInvitation]);
	return (
		<>
			<Button
				type="button"
				title={formatMessage(messages.resend)}
				variant="icon"
				disabled={isLoading}
				onClick={onResend}
			>
				<FaRedo />
			</Button>
			<Button
				type="button"
				title={formatMessage(messages.delete)}
				variant="icon"
				disabled={isLoading}
				onClick={onDelete}
			>
				<FaTrash />
			</Button>
		</>
	);
};
